import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { projectNotificationRows } from "@/lib/notificationLinks";

/**
 * Drives the Notification Center bell.
 *
 *   loading     — first-load flag
 *   rows        — most recent N rows in the caller's scope
 *   unread      — canonical scalar from rpc_get_my_unread_notification_count
 *   markSeen()  — marks only the active organization's notifications seen
 *   refresh()   — pulls rows + unread count again (optimistic on markSeen)
 *
 * Polls every 60s while the caller is logged in. Also exposes `markSeen`
 * so the bell dropdown can clear the badge on open without waiting for
 * the next poll.
 */
export default function useNotificationCenter(
  profileId,
  role,
  organizationId,
  limit = 20,
) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const aliveRef = useRef(true);
  const organizationRef = useRef(organizationId);
  const requestRef = useRef(0);

  organizationRef.current = organizationId;

  const refresh = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (!profileId || !organizationId || !isSupabaseConfigured) {
      if (aliveRef.current && requestId === requestRef.current) {
        setRows([]);
        setUnread(0);
        setLoading(false);
      }
      return;
    }
    try {
      const [listRes, countRes] = await Promise.all([
        supabase.rpc("rpc_list_my_notifications", {
          p_limit: limit,
          p_organization_id: organizationId,
        }),
        supabase.rpc("rpc_get_my_unread_notification_count", {
          p_organization_id: organizationId,
        }),
      ]);
      if (
        !aliveRef.current ||
        requestId !== requestRef.current ||
        organizationRef.current !== organizationId
      ) return;
      if (!listRes.error) {
        const projected = projectNotificationRows(listRes.data, { role });
        setRows(projected);
      }
      if (!countRes.error) {
        const n = typeof countRes.data === "number" ? countRes.data : Number(countRes.data) || 0;
        setUnread(n);
      }
    } catch {
      /* Nav badge must never crash the shell */
    } finally {
      if (
        aliveRef.current &&
        requestId === requestRef.current &&
        organizationRef.current === organizationId
      ) setLoading(false);
    }
  }, [limit, organizationId, profileId, role]);

  useEffect(() => {
    aliveRef.current = true;
    requestRef.current += 1;
    setRows([]);
    setUnread(0);
    setLoading(true);
    refresh();
    const id = setInterval(refresh, 60 * 1000);
    return () => {
      aliveRef.current = false;
      requestRef.current += 1;
      clearInterval(id);
    };
  }, [refresh]);

  const markSeen = useCallback(async () => {
    const expectedOrganizationId = organizationId;
    if (
      !aliveRef.current ||
      !profileId ||
      !expectedOrganizationId ||
      organizationRef.current !== expectedOrganizationId
    ) return;

    // Optimistic: zero the badge + clear is_unread flags immediately.
    setUnread(0);
    setRows((prev) => prev.map((r) => ({ ...r, is_unread: false })));
    if (!isSupabaseConfigured) return;
    try {
      const { error } = await supabase.rpc("rpc_mark_notifications_seen", {
        p_organization_id: expectedOrganizationId,
      });
      if (error) throw error;
    } catch {
      // Restore authoritative state only if the caller is still in the same
      // center. An older center request must never repaint the current shell.
      if (
        aliveRef.current &&
        organizationRef.current === expectedOrganizationId
      ) refresh();
    }
  }, [organizationId, profileId, refresh]);

  return { loading, rows, unread, refresh, markSeen };
}
